// src/components/notifications/NotificationToast.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Button } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import type { QueuedNotificationToast } from '../../hooks/useNotificationToast';

// Every toast that reaches this component is a "System"-category push (the
// backend only ever emits ReceiveNotification for that category - see
// useNotificationSocket), so a single info-styled card covers every case;
// there's no per-category branching to do here.
const AUTO_DISMISS_MS = 5000;
const EXIT_DURATION_MS = 200;

interface NotificationToastProps {
  notification: QueuedNotificationToast | null;
  onClose: () => void;
  onMarkAsRead: () => void;
}

const slideInUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const slideOutDown = keyframes`
  from { transform: translateY(0);    opacity: 1; }
  to   { transform: translateY(16px); opacity: 0; }
`;

const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'exiting',
})<{ exiting: boolean }>(({ theme, exiting }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 9999,
  width: 340,
  maxWidth: 'calc(100vw - 32px)',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  boxShadow: theme.shadows[8],
  borderLeft: `4px solid ${theme.palette.info.main}`,
  padding: '12px 8px 10px 14px',
  animation: `${exiting ? slideOutDown : slideInUp} ${EXIT_DURATION_MS}ms ease-out forwards`,
}));

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
});

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onMarkAsRead,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedRef = useRef(false);

  const clearAutoDismissTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Plays the slide-out animation against whatever is currently displayed,
  // then - only once that's finished - runs the callback that actually
  // advances the queue. This is what lets the exit animation complete
  // before the parent swaps (or clears) the `notification` prop.
  const beginExit = useCallback((after: () => void) => {
    clearAutoDismissTimer();
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setIsExiting(false);
      after();
    }, EXIT_DURATION_MS);
  }, []);

  const startAutoDismissTimer = useCallback(() => {
    clearAutoDismissTimer();
    timerRef.current = setTimeout(() => {
      beginExit(onClose);
    }, AUTO_DISMISS_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, beginExit]);

  // Fresh enter + fresh timer each time a *different* notification becomes
  // current (id changes) - not on every render.
  useEffect(() => {
    if (!notification) return undefined;
    pausedRef.current = false;
    setIsExiting(false);
    startAutoDismissTimer();
    return () => {
      clearAutoDismissTimer();
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.id]);

  const handlePause = () => {
    pausedRef.current = true;
    clearAutoDismissTimer();
  };

  const handleResume = () => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    startAutoDismissTimer();
  };

  const handleClose = () => beginExit(onClose);
  const handleMarkAsRead = () => beginExit(onMarkAsRead);

  if (!notification) return null;

  return (
    <Card
      exiting={isExiting}
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      onFocus={handlePause}
      onBlur={handleResume}
      role="status"
      aria-live="polite"
    >
      <HeaderRow>
        <InfoIcon
          sx={{
            color: 'info.main',
            fontSize: '1.3rem',
            mt: '2px',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {notification.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: '2px' }}>
            {notification.body}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Dismiss notification"
          sx={{ mt: '-4px', mr: '-4px', flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </HeaderRow>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '4px' }}>
        <Button
          size="small"
          startIcon={<DoneAllIcon />}
          onClick={handleMarkAsRead}
        >
          Mark as read
        </Button>
      </Box>
    </Card>
  );
};

export default NotificationToast;
